import type { Workflow } from '@/constants/full-service'

import { netsuiteShopifyWorkflows } from '@/constants/full-service'

type Tag =
    | 'order'
    | 'customer'
    | 'company'
    | 'contact'
    | 'lead'
    | 'deal'
    | 'product'
    | 'inventory'
    | 'invoice'
    | 'payment'
    | 'payout'
    | 'refund'
    | 'shipment'
    | 'ticket'
    | 'subscription'
    | 'bill'

interface AppObject {
    tag: Tag
    plural: string
    title: string
}

const objects: Record<string, Array<AppObject>> = {
    'NetSuite': [
        { tag: 'order', plural: 'sales orders', title: 'Sales Orders' },
        { tag: 'customer', plural: 'customers', title: 'Customers' },
        { tag: 'product', plural: 'inventory items', title: 'Items' },
        { tag: 'inventory', plural: 'stock quantities', title: 'Inventory' },
        { tag: 'invoice', plural: 'invoices', title: 'Invoices' },
        { tag: 'shipment', plural: 'item fulfillments', title: 'Fulfillments' },
        { tag: 'payment', plural: 'customer payments', title: 'Payments' },
        { tag: 'refund', plural: 'credit memos', title: 'Credit Memos' },
        { tag: 'bill', plural: 'vendor bills', title: 'Vendor Bills' }
    ],
    'Shopify': [
        { tag: 'order', plural: 'orders', title: 'Orders' },
        { tag: 'customer', plural: 'customers', title: 'Customers' },
        { tag: 'product', plural: 'products', title: 'Products' },
        { tag: 'inventory', plural: 'inventory levels', title: 'Inventory' },
        { tag: 'shipment', plural: 'fulfillments', title: 'Fulfillments' },
        { tag: 'refund', plural: 'refunds', title: 'Refunds' },
        { tag: 'payment', plural: 'order transactions', title: 'Transactions' },
        { tag: 'payout', plural: 'payouts', title: 'Payouts' }
    ],
    'QuickBooks Online': [
        { tag: 'invoice', plural: 'invoices', title: 'Invoices' },
        { tag: 'customer', plural: 'customers', title: 'Customers' },
        { tag: 'order', plural: 'sales receipts', title: 'Sales Receipts' },
        { tag: 'product', plural: 'items', title: 'Items' },
        { tag: 'payment', plural: 'payments', title: 'Payments' },
        { tag: 'refund', plural: 'refund receipts', title: 'Refund Receipts' },
        { tag: 'bill', plural: 'bills', title: 'Bills' },
        { tag: 'payout', plural: 'deposits', title: 'Deposits' }
    ],
    'Salesforce': [
        { tag: 'deal', plural: 'opportunities', title: 'Opportunities' },
        { tag: 'company', plural: 'accounts', title: 'Accounts' },
        { tag: 'contact', plural: 'contacts', title: 'Contacts' },
        { tag: 'lead', plural: 'leads', title: 'Leads' },
        { tag: 'ticket', plural: 'cases', title: 'Cases' },
        { tag: 'order', plural: 'orders', title: 'Orders' },
        { tag: 'product', plural: 'products', title: 'Products' }
    ],
    'HubSpot': [
        { tag: 'deal', plural: 'deals', title: 'Deals' },
        { tag: 'company', plural: 'companies', title: 'Companies' },
        { tag: 'contact', plural: 'contacts', title: 'Contacts' },
        { tag: 'ticket', plural: 'tickets', title: 'Tickets' },
        { tag: 'product', plural: 'products', title: 'Products' }
    ],
    'WooCommerce': [
        { tag: 'order', plural: 'orders', title: 'Orders' },
        { tag: 'customer', plural: 'customers', title: 'Customers' },
        { tag: 'product', plural: 'products', title: 'Products' },
        { tag: 'inventory', plural: 'stock levels', title: 'Stock' },
        { tag: 'refund', plural: 'refunds', title: 'Refunds' }
    ],
    'BigCommerce': [
        { tag: 'order', plural: 'orders', title: 'Orders' },
        { tag: 'customer', plural: 'customers', title: 'Customers' },
        { tag: 'product', plural: 'products', title: 'Products' },
        { tag: 'inventory', plural: 'inventory levels', title: 'Inventory' },
        { tag: 'shipment', plural: 'shipments', title: 'Shipments' },
        { tag: 'refund', plural: 'refunds', title: 'Refunds' }
    ],
    'Stripe': [
        { tag: 'payment', plural: 'charges', title: 'Charges' },
        { tag: 'customer', plural: 'customers', title: 'Customers' },
        { tag: 'invoice', plural: 'invoices', title: 'Invoices' },
        { tag: 'payout', plural: 'payouts', title: 'Payouts' },
        { tag: 'refund', plural: 'refunds', title: 'Refunds' },
        { tag: 'subscription', plural: 'subscriptions', title: 'Subscriptions' }
    ],
    'ShipStation': [
        { tag: 'order', plural: 'orders', title: 'Orders' },
        { tag: 'shipment', plural: 'shipments', title: 'Shipments' },
        { tag: 'product', plural: 'products', title: 'Products' }
    ],
    'Zendesk': [
        { tag: 'ticket', plural: 'tickets', title: 'Tickets' },
        { tag: 'contact', plural: 'users', title: 'Users' },
        { tag: 'company', plural: 'organizations', title: 'Organizations' }
    ],
    'Xero': [
        { tag: 'invoice', plural: 'invoices', title: 'Invoices' },
        { tag: 'customer', plural: 'contacts', title: 'Contacts' },
        { tag: 'product', plural: 'items', title: 'Items' },
        { tag: 'payment', plural: 'payments', title: 'Payments' },
        { tag: 'bill', plural: 'bills', title: 'Bills' },
        { tag: 'payout', plural: 'bank transactions', title: 'Bank Transactions' },
        { tag: 'refund', plural: 'credit notes', title: 'Credit Notes' }
    ]
}

// Which kinds of source records can become which kinds of destination records.
const targetsFor: Record<Tag, Array<Tag>> = {
    order: ['order', 'invoice'],
    deal: ['order', 'invoice', 'deal'],
    customer: ['customer', 'contact', 'company'],
    contact: ['customer', 'contact'],
    company: ['customer', 'company'],
    lead: ['lead', 'contact', 'customer'],
    product: ['product'],
    inventory: ['inventory'],
    invoice: ['invoice'],
    payment: ['payment'],
    payout: ['payout'],
    refund: ['refund'],
    shipment: ['shipment'],
    ticket: ['ticket'],
    subscription: ['invoice', 'subscription'],
    bill: ['bill']
}

const shortName = (app: string): string => (app === 'QuickBooks Online' ? 'QuickBooks' : app)

const describe = (source: string, sourceObject: AppObject, destination: string, destinationObject: AppObject): string => {
    const from = `${shortName(source)} ${sourceObject.plural}`
    const to = `${shortName(destination)} ${destinationObject.plural}`

    switch (sourceObject.tag) {
        case 'inventory':
            return `Update ${to} from ${from}`
        case 'customer':
        case 'contact':
        case 'company':
            return `Sync ${from} into ${to}`
        case 'payment':
            return `Record ${from} as ${to}`
        case 'shipment':
            return `Update ${to} from ${from} with tracking`
        default:
            return `Create ${to} from ${from}`
    }
}

const slackAlerts: Record<Tag, (app: string, plural: string) => string> = {
    order: (app, plural) => `Post new ${app} ${plural} to a Slack channel`,
    deal: (app, plural) => `Notify Slack when ${app} ${plural} change stage`,
    ticket: (app, plural) => `Alert Slack when urgent ${app} ${plural} arrive`,
    payment: (app, plural) => `Post failed ${app} ${plural} to a Slack channel`,
    inventory: (app, plural) => `Warn a Slack channel when ${app} ${plural} run low`,
    customer: (app, plural) => `Announce new ${app} ${plural} in Slack`,
    contact: (app, plural) => `Announce new ${app} ${plural} in Slack`,
    company: (app, plural) => `Announce new ${app} ${plural} in Slack`,
    lead: (app, plural) => `Route new ${app} ${plural} to the right Slack channel`,
    invoice: (app, plural) => `Send overdue ${app} ${plural} to a Slack channel`,
    shipment: (app, plural) => `Flag delayed ${app} ${plural} in Slack`,
    refund: (app, plural) => `Post ${app} ${plural} to Slack for review`,
    payout: (app, plural) => `Share daily ${app} ${plural} totals in Slack`,
    product: (app, plural) => `Post new ${app} ${plural} to a Slack channel`,
    subscription: (app, plural) => `Notify Slack when ${app} ${plural} are cancelled`,
    bill: (app, plural) => `Send ${app} ${plural} to Slack for approval`
}

const fromSlack: Array<Tag> = ['ticket', 'lead', 'deal', 'contact', 'company', 'customer']

const between = (source: string, destination: string): Array<Workflow> => {
    if (destination === 'Slack') {
        return (objects[source] ?? []).map(object => ({
            title: `${object.title} Alerts`,
            description: slackAlerts[object.tag](shortName(source), object.plural),
            from: source,
            to: destination
        }))
    }

    if (source === 'Slack') {
        return (objects[destination] ?? [])
            .filter(object => fromSlack.includes(object.tag))
            .map(object => ({
                title: object.title,
                description: `Create ${shortName(destination)} ${object.plural} from flagged Slack messages`,
                from: source,
                to: destination
            }))
    }

    const results: Array<Workflow> = []

    ;(objects[source] ?? []).forEach(sourceObject => {
        ;(objects[destination] ?? []).forEach(destinationObject => {
            if (!targetsFor[sourceObject.tag].includes(destinationObject.tag)) return

            results.push({
                title: destinationObject.title,
                description: describe(source, sourceObject, destination, destinationObject),
                from: source,
                to: destination
            })
        })
    })

    return results
}

const MAX_WORKFLOWS = 27

// Example workflows for any pair of apps: hand-written for NetSuite and Shopify, assembled from each app's records otherwise.
export const workflowsFor = (first: string, second: string): Array<Workflow> => {
    const pair = [first, second].sort().join('|')

    if (pair === 'NetSuite|Shopify') return netsuiteShopifyWorkflows

    const forward = between(first, second)
    const backward = between(second, first)
    const mixed: Array<Workflow> = []
    const seen = new Set<string>()

    for (let index = 0; index < Math.max(forward.length, backward.length); index += 1) {
        ;[forward[index], backward[index]].forEach(workflow => {
            if (workflow && !seen.has(workflow.description)) {
                seen.add(workflow.description)
                mixed.push(workflow)
            }
        })
    }

    if (mixed.length === 0) {
        mixed.push({
            title: "Custom Workflow",
            description: `Design a custom workflow between ${shortName(first)} and ${shortName(second)} with our team`,
            from: first,
            to: second
        })
    }

    return mixed.slice(0, MAX_WORKFLOWS)
}

export const defaultPartner: Record<string, string> = {
    'NetSuite': 'Shopify',
    'Shopify': 'NetSuite',
    'QuickBooks Online': 'Shopify',
    'Salesforce': 'NetSuite',
    'HubSpot': 'QuickBooks Online',
    'WooCommerce': 'NetSuite',
    'BigCommerce': 'NetSuite',
    'Stripe': 'QuickBooks Online',
    'Slack': 'HubSpot',
    'ShipStation': 'Shopify',
    'Zendesk': 'Salesforce',
    'Xero': 'Shopify'
}

export { shortName }
